import { Injectable } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { combineAllWith } from '@shared/combineAllWith';
import { readFiles } from '@shared/fileReaderUtils';
import { filterMap } from '@shared/utils/rxjs';
import { catchError, concatAll, forkJoin, from, map, mergeMap, Observable, of, toArray } from 'rxjs';
import { ExcelFileTransfer, xlsxImportError } from '../file-transfer-services/excelFileTransfer.service';
import { IFileDataSchema, IImportedData } from '../file-transfer-services/interfaces';
import { ICompletionFlow, ICompletionGroupType, IPostTerminalRequest, IPrintConfiguration, IPutTerminalRequest, ITerminal, TerminalAdministrationService } from '../terminalAdministration.service';
import { TerminalTemplateAdministrationService } from '../terminaltemplateAdministration.service';
import { TerminalValidationService } from '../Validation/terminalValidation.service';
import { WorktaskService } from '../worktask.service';
import { FailedParse, ImportErrorTypes, ImportResponse, ImportStatus, ITransferService, SuccessFullImport } from './interfaces';

const sheetName = 'Terminals';
const templateFileName = 'terminal_import_template';
const translationPrefix = 'mes.administration.terminal.import';

/** Cap on in-flight create/update calls */
const importConcurrency = 5;

export interface ITerminalExportData {
	alias: string;
	name: string | null;
	terminalTemplateAlias: string | null;
	workTaskAlias: string | null;
	completionFlow: string | null;
	completionGroupType: string | null;
	stationFilter: string | null;
	workTaskCategories: string[];
	incidentCodeGroups: string[];
	sqlQuery: string | null;
	nextTerminalAlias: string | null;
}

/** The single source of truth for terminal columns — export, import, and template generation all read it. */
export const TerminalSchema: IFileDataSchema<ITerminalExportData> = {
	name: sheetName,
	properties: {
		alias: {
			type: 'string',
			required: true,
			width: 20,
			comment: `${translationPrefix}.template-help.alias`,
			example: 'EXAMPLE_TERMINAL',
		},
		name: {
			type: 'string',
			required: false,
			width: 30,
			comment: `${translationPrefix}.template-help.name`,
			example: 'Example Terminal',
		},
		terminalTemplateAlias: {
			type: 'string',
			required: false,
			width: 25,
			comment: `${translationPrefix}.template-help.terminalTemplateAlias`,
			example: 'TEMPLATE_ALIAS',
		},
		workTaskAlias: {
			type: 'string',
			required: false,
			width: 20,
			comment: `${translationPrefix}.template-help.workTaskAlias`,
			example: 'WORKTASK_ALIAS',
		},
		completionFlow: {
			type: 'string',
			required: false,
			width: 30,
			comment: `${translationPrefix}.template-help.completionFlow`,
			example: ICompletionFlow.ItemCompletionOnStartFlow,
		},
		completionGroupType: {
			type: 'string',
			required: false,
			width: 30,
			comment: `${translationPrefix}.template-help.completionGroupType`,
			example: ICompletionGroupType.Groups,
		},
		stationFilter: {
			type: 'string',
			required: false,
			width: 20,
			comment: `${translationPrefix}.template-help.stationFilter`,
			// A whole number, not a station name: the dialog validates maxLength(2) + WholeNumber,
			// so the previous template's 'Station1' example produced a file failing its own import.
			example: '1',
		},
		workTaskCategories: {
			type: { array: 'string' },
			required: false,
			width: 30,
			comment: `${translationPrefix}.template-help.workTaskCategories`,
			example: ['Category1', 'Category2'],
		},
		incidentCodeGroups: {
			type: { array: 'string' },
			required: false,
			width: 30,
			comment: `${translationPrefix}.template-help.incidentCodeGroups`,
			example: ['Group1', 'Group2'],
		},
		sqlQuery: {
			type: 'string',
			required: false,
			width: 50,
			comment: `${translationPrefix}.template-help.sqlQuery`,
			example: 'SELECT * FROM table WHERE condition = true',
		},
		nextTerminalAlias: {
			type: 'string',
			required: false,
			width: 25,
			comment: `${translationPrefix}.template-help.nextTerminalAlias`,
			example: 'NEXT_TERMINAL_ALIAS',
		},
	},
};

export interface TerminalImportContext {
	/** Keyed by alias, and kept current as phase 1 creates and updates terminals. */
	terminalsByAlias: Map<string, ITerminal>;
	templateIdByAlias: Map<string, string>;
	workTaskIdByAlias: Map<string, string>;
	/** Alias values already seen in the imported file, to reject duplicates. */
	seenAliases: Set<string>;
}

/** A row that resolved its references and passed validation, awaiting phase 1. */
export interface TerminalImportCandidate {
	/** `nextId` is always `null` here — the link is applied in phase 2. `sqlQuery` is the raw, un-encoded string; the admin service base64-encodes whatever object it is handed **in place**, so this is never passed to it directly. */
	terminal: ITerminal;
	nextTerminalAlias: string | null;
	location?: string;
}

/** A row whose phase-1 create/update succeeded. Reported only once phase 2 has settled too. */
export interface TerminalImportResult {
	/**
	 * The persisted terminal exactly as the API returned it, so phase 2 needs no lookup of its own: the assigned `id` and the `printConfigurations` the import cannot set are both already here.
	 *
	 * `sqlQuery` is the one field taken from the row rather than the response, because the admin service base64-encodes the request **in place** — so the value the response echoes is the encoded one, and re-sending it in phase 2 would encode it a second time.
	 */
	entity: ITerminal;
	status: ImportStatus.SuccessCreate | ImportStatus.SuccessUpdate;
	nextTerminalAlias: string | null;
	location?: string;
}

/** A blank or whitespace-only cell becomes `null` rather than `''`, because `ObjectValidators` treats only `null`/`undefined` as missing — so this is what lets the validation service report a blank mandatory column per field instead of letting `''` through as a value. */
function trimToNull(value: string | number | null | undefined): string | null {
	if (value === null || value === undefined) {
		return null;
	}

	const trimmed = `${value}`.trim();
	return trimmed.length === 0 ? null : trimmed;
}

/**
 * Blank segments are dropped and an absent or empty cell becomes `[]`, never `undefined`.
 *
 * Both rules live here rather than in the shared `convertPropertyToType`, which splits on `,` with no blank filter: changing it would silently alter `MesDepartmentSchema.shiftApprovers` and `MesWorkTaskSchema.tags`.
 */
function toStringList(value: string | string[] | null | undefined): string[] {
	if (value === null || value === undefined) {
		return [];
	}

	const segments = Array.isArray(value) ? value : value.split(',');
	return segments.map((segment) => segment.trim()).filter((segment) => segment.length > 0);
}

@Injectable({
	providedIn: 'root',
})
export class TerminalTransferService implements ITransferService<ITerminal> {
	constructor(
		private terminalService: TerminalAdministrationService,
		private terminalTemplateService: TerminalTemplateAdministrationService,
		private workTaskService: WorktaskService,
		private terminalValidationService: TerminalValidationService,
		private excelFileTransfer: ExcelFileTransfer<ITerminalExportData>,
		private translator: TranslateService,
	) {}

	public hasTemplate = () => true;

	public getTemplate = (): void => {
		this.excelFileTransfer.generateTemplate(templateFileName, TerminalSchema);
	};

	public exportData(terminals: ITerminal[]): Observable<ITerminal[]> {
		return forkJoin({
			templates: this.terminalTemplateService.getTerminalTemplates(),
			workTasks: this.workTaskService.getWorktasks(),
		}).pipe(
			map(({ templates, workTasks }) => {
				const templateAliasById = new Map<string, string>();
				templates.result.forEach((template) => templateAliasById.set(template.id, template.alias));

				const workTaskAliasById = new Map<string, string>();
				workTasks.result.forEach((workTask) => workTaskAliasById.set(workTask.id, workTask.alias));

				const terminalAliasById = new Map<string, string>();
				terminals.forEach((terminal) => {
					if (terminal.id) {
						terminalAliasById.set(terminal.id, terminal.alias);
					}
				});

				// Row order carries no meaning: the import applies every `nextId` link in a second phase
				// that begins only after every create/update has settled, so a reference resolves
				// wherever its target sits in the file. Terminals are written in whatever order the API
				// returned them.
				return terminals.map((terminal): ITerminalExportData => ({
					alias: terminal.alias,
					name: terminal.name,
					terminalTemplateAlias: terminal.terminalTemplateId ? (templateAliasById.get(terminal.terminalTemplateId) ?? null) : null,
					workTaskAlias: terminal.workTaskId ? (workTaskAliasById.get(terminal.workTaskId) ?? null) : null,
					completionFlow: terminal.completionFlow ?? null,
					completionGroupType: terminal.completionGroupType ?? ICompletionGroupType.Groups,
					stationFilter: terminal.stationFilter ?? null,
					workTaskCategories: terminal.workTaskCategories ?? [],
					incidentCodeGroups: terminal.incidentCodeGroups ?? [],
					sqlQuery: terminal.sqlQuery ?? null,
					nextTerminalAlias: terminal.nextId ? (terminalAliasById.get(terminal.nextId) ?? null) : null,
				}));
			}),
			mergeMap((rows) => {
				// The filename is date-suffixed; the sheet is always named after the schema, so the
				// exported file still imports.
				const dateStamp = new Date().toISOString().substring(0, 10);
				return this.excelFileTransfer.exportData(`terminals_${dateStamp}`, rows, TerminalSchema);
			}),
			map(() => terminals),
			catchError((error) => {
				throw new Error(`Failed to export data: ${error.message}`);
			}),
		);
	}

	importData(file: File | File[], overwrite: boolean = false): Observable<ImportResponse> {
		const ctx: TerminalImportContext = {
			terminalsByAlias: new Map<string, ITerminal>(),
			templateIdByAlias: new Map<string, string>(),
			workTaskIdByAlias: new Map<string, string>(),
			seenAliases: new Set<string>(),
		};

		// Threaded by closure and only ever pushed to — every stage below reports into this array,
		// so a failure at any stage reaches the user instead of being discarded.
		const ImportErrors: ImportErrorTypes[] = [];
		const files = Array.isArray(file) ? file : [file];

		const candidates = readFiles(files, 'binary').pipe(
			mergeMap(this.importFileData(ImportErrors)),
			combineAllWith(
				forkJoin([
					// Without this an arrive/leave terminal is invisible, so a re-import duplicates its alias
					this.terminalService.getTerminals({ includeArriveLeaveTerminals: true }),
					this.terminalTemplateService.getTerminalTemplates(),
					this.workTaskService.getWorktasks(),
				]).pipe(
					map(([terminals, templates, workTasks]) => {
						terminals.result.forEach((terminal) => ctx.terminalsByAlias.set(terminal.alias, terminal));
						templates.result.forEach((template) => ctx.templateIdByAlias.set(template.alias, template.id));
						workTasks.result.forEach((workTask) => ctx.workTaskIdByAlias.set(workTask.alias, workTask.id));
						return {}; // nothing to emit — this only fills the context
					}),
				),
			),
			map(([row]) => row),
			filterMap(this.mapReferences(ctx, ImportErrors)),
			filterMap(this.validateTerminal(ImportErrors)),
		);

		// Split rather than one long chain: rxjs types `pipe` up to 9 operators and silently degrades
		// the result to `Observable<unknown>` beyond that.
		return candidates.pipe(
			mergeMap(this.createOrUpdateTerminal(ctx, overwrite, ImportErrors), importConcurrency),
			// Barrier: phase 2 must not start until every create/update has settled, so a
			// nextTerminalAlias resolves wherever its target sat in the file. concatAll then puts the
			// settled results back on the stream so phase 2 can be a per-row stage like phase 1.
			toArray(),
			concatAll(),
			// Phase 2 — kept local to this service rather than added as an ITransferService hook:
			// terminals are the only self-referencing entity in the domain.
			mergeMap(this.linkNextTerminal(ctx, ImportErrors), importConcurrency),
			toArray(),
			map(
				(results) =>
					({
						successfulImports: results.map(
							(result) =>
								({
									Id: result.entity.alias,
									status: result.status,
								}) as SuccessFullImport,
						),
						errors: ImportErrors,
					}) as ImportResponse,
			),
		);
	}

	importFileData(ImportErrors: ImportErrorTypes[]) {
		return (buffer: ArrayBuffer): Observable<IImportedData<ITerminalExportData>> => {
			const errorList: xlsxImportError[] = [];

			try {
				const rows = this.excelFileTransfer.importData(buffer, TerminalSchema, errorList);

				errorList.forEach((e) => {
					ImportErrors.push({
						status: ImportStatus.ParsingError,
						message: e.error,
						// The row number is already inside e.error — every message ExcelFileTransfer emits
						// names its own row, so FailedParse stays format-agnostic.
					} as FailedParse);
				});

				return from(rows);
			} catch (error) {
				ImportErrors.push({
					status: ImportStatus.ParsingError,
					message: error.message,
				} as FailedParse);
				return of();
			}
		};
	}

	/**
	 * Resolves the file's alias columns to ids. A row whose `terminalTemplateAlias` or `workTaskAlias` cannot be resolved is **dropped**, not merely reported. The previous implementation reported the error and imported the row anyway with the reference nulled, which on a re-import with overwrite ticked silently
	 * replaced a working terminal's references with defaults — so a single mistyped alias destroyed existing data.
	 */
	mapReferences(ctx: TerminalImportContext, ImportErrors: ImportErrorTypes[]) {
		return (imported: IImportedData<ITerminalExportData>): TerminalImportCandidate | undefined => {
			const { data: row, location } = imported;
			const alias = trimToNull(row.alias);
			const errors: Record<string, ValidationErrors> = {};

			if (alias !== null && ctx.seenAliases.has(alias)) {
				ImportErrors.push({
					Id: alias,
					location,
					status: ImportStatus.ValidationError,
					errors: {
						alias: {
							duplicate: this.translator.instant(`${translationPrefix}.errors.duplicate-alias`, { alias }),
						},
					},
				});
				return undefined;
			}

			if (alias !== null) {
				ctx.seenAliases.add(alias);
			}

			const templateAlias = trimToNull(row.terminalTemplateAlias);
			let terminalTemplateId: string | null = null;
			if (templateAlias !== null) {
				terminalTemplateId = ctx.templateIdByAlias.get(templateAlias) ?? null;
				if (terminalTemplateId === null) {
					errors['terminalTemplateAlias'] = {
						notFound: this.translator.instant(`${translationPrefix}.errors.template-alias-not-found`, { alias: templateAlias }),
					};
				}
			}

			const workTaskAlias = trimToNull(row.workTaskAlias);
			let workTaskId: string | null = null;
			if (workTaskAlias !== null) {
				workTaskId = ctx.workTaskIdByAlias.get(workTaskAlias) ?? null;
				if (workTaskId === null) {
					errors['workTaskAlias'] = {
						notFound: this.translator.instant(`${translationPrefix}.errors.worktask-alias-not-found`, { alias: workTaskAlias }),
					};
				}
			}

			if (Object.keys(errors).length > 0) {
				ImportErrors.push({
					Id: alias ?? this.translator.instant(`${translationPrefix}.errors.missing-alias`),
					location,
					status: ImportStatus.ValidationError,
					errors,
				});
				return undefined;
			}

			return {
				// Blank values are carried as null rather than '' on purpose: `ObjectValidators`
				// treats null as missing, so the validation service reports them per field.
				terminal: {
					id: null,
					alias,
					name: trimToNull(row.name),
					terminalTemplateId,
					workTaskId,
					completionFlow: trimToNull(row.completionFlow) as ICompletionFlow | null,
					// A blank cell means Groups, as it does in the API. An unknown value is carried
					// through as-is so the validator rejects it by name instead of silently defaulting.
					completionGroupType: (trimToNull(row.completionGroupType) ?? ICompletionGroupType.Groups) as ICompletionGroupType,
					stationFilter: trimToNull(row.stationFilter),
					workTaskCategories: toStringList(row.workTaskCategories),
					incidentCodeGroups: toStringList(row.incidentCodeGroups),
					// Raw. The admin service encodes in place, so this must never be handed over.
					sqlQuery: row.sqlQuery ?? '',
					nextId: null,
					printConfigurations: [],
				} as ITerminal,
				nextTerminalAlias: trimToNull(row.nextTerminalAlias),
				location,
			};
		};
	}

	validateTerminal(ImportErrors: ImportErrorTypes[]) {
		return (candidate: TerminalImportCandidate): TerminalImportCandidate | undefined => {
			// The id is assigned by the backend, so it is not validated on import.
			const validationErrors = this.terminalValidationService.validateTerminal(candidate.terminal, false);
			if (Object.keys(validationErrors).length > 0) {
				ImportErrors.push({
					Id: candidate.terminal.alias ?? this.translator.instant(`${translationPrefix}.errors.missing-alias`),
					location: candidate.location,
					status: ImportStatus.ValidationError,
					errors: validationErrors,
				});
				return undefined;
			}

			return candidate;
		};
	}

	/** Phase 1 — create or update the terminal with `nextId: null`. A failed call is reported as `ImportStatus.Failed` naming the alias; the row emits nothing, so it is never counted as a success. */
	createOrUpdateTerminal(ctx: TerminalImportContext, overwrite: boolean, ImportErrors: ImportErrorTypes[]) {
		return (candidate: TerminalImportCandidate): Observable<TerminalImportResult> => {
			const terminal = candidate.terminal;
			const existing = ctx.terminalsByAlias.get(terminal.alias);

			if (existing) {
				if (!overwrite) {
					ImportErrors.push({
						Id: terminal.alias,
						location: candidate.location,
						status: ImportStatus.Skipped,
						message: this.translator.instant(`${translationPrefix}.errors.exists`, {
							alias: terminal.alias,
						}),
					});
					return of();
				}

				// printConfigurations is not importable and must survive an update.
				const printConfigurations = existing.printConfigurations ?? [];
				// Carry the persisted nextId through phase 1 rather than nulling it. Phase 2
				// overwrites it only on success, so a phase-2 failure (unresolvable alias, or a
				// rejected PUT) leaves the terminal's existing link intact instead of destroying
				// it. Nulling here would make a single typo'd nextTerminalAlias silently break a
				// working flow chain, and the phase-2 message promises only that the new link was
				// not set — it cannot restore an old one.
				const request: IPutTerminalRequest = {
					id: existing.id,
					...this.buildTerminalPayload(terminal, existing.nextId ?? null, printConfigurations),
				};

				return this.terminalService.updateTerminal(request).pipe(
					map((persisted) => {
						ctx.terminalsByAlias.set(terminal.alias, persisted);

						return {
							entity: { ...persisted, sqlQuery: terminal.sqlQuery },
							status: ImportStatus.SuccessUpdate,
							nextTerminalAlias: candidate.nextTerminalAlias,
							location: candidate.location,
						} as TerminalImportResult;
					}),
					catchError((error) => {
						ImportErrors.push({
							Id: terminal.alias,
							location: candidate.location,
							status: ImportStatus.Failed,
							message: this.translator.instant(`${translationPrefix}.errors.update-failed`, { alias: terminal.alias, msg: error.message }),
						});
						return of();
					}),
				);
			}

			const request: IPostTerminalRequest = this.buildTerminalPayload(terminal, null, []);

			return this.terminalService.createTerminal(request).pipe(
				map((created) => {
					ctx.terminalsByAlias.set(terminal.alias, created);

					return {
						entity: { ...created, sqlQuery: terminal.sqlQuery },
						status: ImportStatus.SuccessCreate,
						nextTerminalAlias: candidate.nextTerminalAlias,
						location: candidate.location,
					} as TerminalImportResult;
				}),
				catchError((error) => {
					ImportErrors.push({
						Id: terminal.alias,
						location: candidate.location,
						status: ImportStatus.Failed,
						message: this.translator.instant(`${translationPrefix}.errors.create-failed`, { alias: terminal.alias, msg: error.message }),
					});
					return of();
				}),
			);
		};
	}

	/**
	 * Phase 2 — patch in the `nextId` links, so rows that reference each other resolve regardless of file order, including a reference to a terminal a later row created.
	 *
	 * No row is reported before both of its phases have settled. There is no rollback available across the two calls, so a phase-1 success with a phase-2 failure is reported as `Failed` with a message saying the terminal exists but its link does not, and telling the user to re-run with overwrite ticked — a bare re-run
	 * would find the alias already created by phase 1 and skip the row, never reaching phase 2 again.
	 */
	linkNextTerminal(ctx: TerminalImportContext, ImportErrors: ImportErrorTypes[]) {
		return (result: TerminalImportResult): Observable<TerminalImportResult> => {
			const terminal = result.entity;
			const nextAlias = result.nextTerminalAlias;

			if (nextAlias === null) {
				return of(result);
			}

			// Only the target needs looking up, and no re-read is required for it: phase 1 writes every
			// terminal it creates or updates back into ctx.terminalsByAlias, and the `toArray()` before
			// this stage guarantees all of phase 1 has settled — so the map already holds every terminal
			// that exists, alias to id. This row's own id and printConfigurations came back on
			// `result.entity`, so it needs no lookup of itself.
			const nextTerminal = ctx.terminalsByAlias.get(nextAlias) ?? null;
			const nextId = nextTerminal?.id ?? null;

			if (nextId !== null && !this.terminalService.isProductionTerminal(nextTerminal)) {
				ImportErrors.push({
					Id: terminal.alias,
					location: result.location,
					status: ImportStatus.Failed,
					message: this.translator.instant(`${translationPrefix}.errors.next-terminal-not-production`, { alias: nextAlias }),
				});
				return of();
			}

			if (nextId === null) {
				ImportErrors.push({
					Id: terminal.alias,
					location: result.location,
					status: ImportStatus.Failed,
					message: this.translator.instant(`${translationPrefix}.errors.next-terminal-alias-not-found`, { alias: nextAlias }),
				});
				return of();
			}

			// A freshly built request holding the raw sqlQuery. Reusing phase 1's object would submit a
			// value the admin service had already base64-encoded, encoding it a second time.
			const request: IPutTerminalRequest = {
				id: terminal.id,
				...this.buildTerminalPayload(terminal, nextId, terminal.printConfigurations ?? []),
			};

			return this.terminalService.updateTerminal(request).pipe(
				map(() => result),
				catchError((error) => {
					ImportErrors.push({
						Id: terminal.alias,
						location: result.location,
						status: ImportStatus.Failed,
						message: this.translator.instant(`${translationPrefix}.errors.next-terminal-link-failed`, {
							alias: nextAlias,
							msg: error.message,
						}),
					});
					return of();
				}),
			);
		};
	}

	/**
	 * Builds the mutable half of a create/update request. Called once per HTTP call, never cached: `TerminalAdministrationService` assigns `terminal.sqlQuery = base64EncodeUnicode(...)` on the object it is handed, so a request reused across the two phases would be encoded twice. The arrays are copied for the same
	 * reason.
	 */
	private buildTerminalPayload(terminal: ITerminal, nextId: string | null, printConfigurations: IPrintConfiguration[]) {
		return {
			alias: terminal.alias,
			name: terminal.name,
			terminalTemplateId: terminal.terminalTemplateId,
			workTaskId: terminal.workTaskId,
			completionFlow: terminal.completionFlow,
			completionGroupType: terminal.completionGroupType,
			stationFilter: terminal.stationFilter,
			workTaskCategories: [...terminal.workTaskCategories],
			incidentCodeGroups: [...terminal.incidentCodeGroups],
			// Never null: the admin service's encoder stringifies whatever it is given, so a null
			// here would be stored as the base64 of the text "null".
			sqlQuery: terminal.sqlQuery ?? '',
			nextId,
			printConfigurations: [...printConfigurations],
		};
	}
}
